import React, { useState, useEffect, useCallback } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  Box,
  MenuItem,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Pagination,
  Radio,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import Decrypt from "../../../shared/utils/Decrypt";
import styles from "../../AdminportalFolder/jobBatch/JobBatch.module.css";
import CapitalizeFirstLetter from "../../../shared/utils/CapitalizeFirstLetter";
import socket from "../../../shared/utils/Socket";

// Helper function to check if OTP is expired (more than 10 minutes old)
const isOTPExpired = (generatedAt) => {
  if (!generatedAt) return false;

  const generatedTime = new Date(generatedAt);
  const currentTime = new Date();

  // Calculate difference in minutes
  const diffInMinutes = Math.floor((currentTime - generatedTime) / (1000 * 60));

  return diffInMinutes > 10;
};

// Helper function to get OTP status
const getOTPStatus = (store) => {
  if (store.verifiedAt) {
    return { status: "verified", label: "Verified" };
  } else if (isOTPExpired(store.generatedAt)) {
    return { status: "expired", label: "Expired" };
  } else {
    return { status: "pending", label: "Pending" };
  }
};

// ------------------ Toolbar Component ------------------ //
const EnhancedTableToolbar = ({
  onClientChange,
  onCompanyChange,
  clients,
  companies,
  search,
  setSearch,
  setSelectedCompany,
  selectedCompany,
}) => {
  const [selectedClient, setSelectedClient] = useState("");
  // const [selectedCompany, setSelectedCompany] = useState("");

  return (
    <div className="border-b flex justify-between items-center h-16 px-4 bg-white shadow-sm">
      <h2 className="font-bold text-lg text-gray-700">
        Store OTP Verification
      </h2>

      <div className="flex gap-3">
        <TextField
          sx={{ maxWidth: 250, minWidth: 200 }}
          size="small"
          label="Search Store"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Client */}
        <FormControl size="small" sx={{ maxWidth: 250, minWidth: 200 }}>
          <InputLabel>Select Client</InputLabel>
          <Select
            value={selectedClient}
            label="Select Client"
            onChange={(e) => {
              setSelectedClient(e.target.value);
              onClientChange(e.target.value);
              setSelectedCompany("");
            }}
            renderValue={(selected) => {
              const displayValue = selected || "Select Product";
              return displayValue.length > 30
                ? `${displayValue.substring(0, 30)}...`
                : displayValue;
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 500, // <-- dropdown height
                  width: 200, // <-- dropdown width
                },
              },
            }}
          >
            {clients.map((cl, index) => (
              <MenuItem key={index} value={cl.name} sx={{ px: 0, py: 0.5 }}>
                <Radio
                  checked={selectedClient === cl.name}
                  value={cl.name}
                  size="small"
                />
                <Box className="flex flex-col ml-1 w-full overflow-hidden">
                  {cl.name.length > 30 ? (
                    <Tooltip
                      title={cl.name}
                      placement="top-start"
                      arrow
                      enterDelay={500}
                    >
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full block cursor-default">
                        {`${cl.name.substring(0, 30)}...`}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full block">
                      {cl.name.charAt(0).toUpperCase() + cl.name.slice(1)}
                    </span>
                  )}
                  {cl.email && (
                    <span className="text-xs text-gray-500">{cl.email}</span>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Company */}
        <FormControl size="small" sx={{ maxWidth: 250, minWidth: 200 }}>
          <InputLabel>Select Campaign</InputLabel>
          <Select
            value={selectedCompany}
            disabled={!selectedClient}
            label="Select Campaign"
            onChange={(e) => {
              setSelectedCompany(e.target.value);
              onCompanyChange(e.target.value);
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 500, // <-- dropdown height
                  width: 340, // <-- dropdown width
                },
              },
            }}
            renderValue={(selected) => selected || "Select Campaign"}
          >
            {companies.map((comp) => (
              <MenuItem key={comp.id} value={comp.title}>
                <Radio
                  checked={selectedCompany === comp.title}
                  value={comp.title}
                />
                {comp.title.charAt(0).toUpperCase() + comp.title.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

// ------------------ Main Component ------------------ //
const StoreOTP = () => {
  const [apiClientsData, setApiClientsData] = useState([]); // full API data
  const [clients, setClients] = useState([]); // only names
  const [companies, setCompanies] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");

  // Pagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  const [searchDebounce, setSearchDebounce] = useState("");

  const serverURL = process.env.REACT_APP_URL;

  // Add interval to refresh expired status every minute
  useEffect(() => {
    socket.on("new-otp-received", () => {
      if (selectedCompany) {
        fetchOTPData(selectedCompany, page, rowsPerPage, searchDebounce);
      }
    });

    return () => {
      socket.off("new-otp-received");
    };
  }, [selectedCompany, page, rowsPerPage, searchDebounce]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${serverURL}/campaign/allClientAndCampaigns`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const apiData = response.data.data;
        setApiClientsData(apiData);

        // Extract client names only
        const clientNames = apiData.map((c) => {
          return { name: c.name, email: c.email };
        });
        setClients(clientNames);
      } catch (error) {
        console.error("Failed to fetch clients", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
      if (selectedCompany) {
        setPage(1);
        fetchOTPData(selectedCompany, 1, rowsPerPage, search);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search, selectedCompany]);

  // Fetch OTP data from backend
  const fetchOTPData = useCallback(
    async (campaignTitle, pageNum, limit, searchTerm) => {
      if (!campaignTitle) return;

      setLoading(true);

      const selectedCampaign = companies.find(
        (cmp) => cmp.title === campaignTitle,
      );

      if (selectedCampaign) {
        try {
          const token = localStorage.getItem("token");

          const res = await axios.get(
            `${serverURL}/campaign/allOTPs/${selectedCampaign.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                page: pageNum,
                limit: limit,
                search: searchTerm,
                sortBy: "generatedAt",
                sortOrder: "desc",
              },
            },
          );

          const responseData = res?.data;
          setStores(responseData?.data || []);

          // Update pagination info from backend response
          if (responseData?.pagination) {
            setPaginationInfo({
              currentPage: responseData.pagination.currentPage,
              totalPages: responseData.pagination.totalPages,
              totalItems: responseData.pagination.totalItems,
            });
            setPage(responseData.pagination.currentPage);
          }
        } catch (err) {
          console.error("Campaign store fetch failed", err);
          setStores([]);
          setPaginationInfo({
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
          });
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    },
    [companies, serverURL],
  );

  const handleClientChange = (clientName) => {
    setStores([]);
    setCompanies([]);
    setSelectedClient(clientName);
    setSelectedCompany("");
    setSearch("");
    setSearchDebounce("");
    setPage(1);
    setPaginationInfo({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    });

    // find that client from saved API data
    const selectedClientData = apiClientsData.find(
      (c) => c?.name === clientName,
    );

    if (selectedClientData) {
      const campaignTitles = selectedClientData.campaigns.map((c) => ({
        title: c.campaignTitle,
        id: c.campaignId,
      }));

      // set to second dropdown
      setCompanies(campaignTitles);
    }

    setStores([]);
  };

  const handleCompanyChange = async (campaignTitle) => {
    console.log("Selected Campaign Title:", campaignTitle);
    setSelectedCompany(campaignTitle);
    setPage(1);
    setSearch("");
    setSearchDebounce("");

    // // Fetch data for the selected campaign
    // fetchOTPData(campaignTitle, 1, rowsPerPage, "");
  };

  // Handle page change
  const handlePageChange = async (event, value) => {
    setPage(value);
    if (selectedCompany) {
      fetchOTPData(selectedCompany, value, rowsPerPage, searchDebounce);
    }
  };

  // Handle rows per page change
  const handleRowsPerPageChange = async (event) => {
    const newRowsPerPage = event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPage(1);

    if (selectedCompany) {
      fetchOTPData(selectedCompany, 1, newRowsPerPage, searchDebounce);
    }
  };

  return (
    <div className="p-4 bg-gray-50 min-h-[90vh] flex flex-col">
      <EnhancedTableToolbar
        onClientChange={handleClientChange}
        onCompanyChange={handleCompanyChange}
        clients={clients}
        companies={companies}
        search={search}
        setSelectedCompany={setSelectedCompany}
        selectedCompany={selectedCompany}
        setSearch={setSearch}
      />

      <Paper className="mt-4 shadow-md rounded-md overflow-hidden flex-1 flex flex-col">
        {loading ? (
          <div className="flex justify-center items-center h-[calc(100vh-200px)] p-10">
            <CircularProgress />
          </div>
        ) : stores.length === 0 ? (
          <div className="p-10 text-center flex justify-center items-center h-[calc(100vh-200px)] text-gray-500">
            {selectedClient && selectedCompany
              ? search
                ? "No stores found matching your search"
                : "No OTP Data Found for This Campaign"
              : selectedClient && !selectedCompany
                ? "Select a Campaign to View OTP Data"
                : "Select a Client to View Campaigns"}
          </div>
        ) : (
          <div className="flex flex-col flex-1">
            <div className="flex-1 overflow-hidden">
              <TableContainer
                className={`${styles.tableContainer} ${styles["custom-scrollbar"]}`}
                sx={{
                  height: "100%",
                  maxHeight: "calc(100vh - 280px)",
                }}
              >
                <Table stickyHeader className="rounded-lg">
                  <TableHead>
                    <TableRow className={styles.tableHeader}>
                      <TableCell className={styles.tableCell}>
                        Store Name
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        Address
                      </TableCell>
                      <TableCell className={styles.tableCell}>OTP</TableCell>
                      <TableCell className={styles.tableCell}>Status</TableCell>
                      <TableCell className={styles.tableCell}>
                        Generated By
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        Generated At
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        Verified At
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {stores?.map((store) => {
                      const statusInfo = getOTPStatus(store);

                      return (
                        <TableRow hover key={store._id}>
                          <TableCell>
                            <div>
                              <div>
                                {CapitalizeFirstLetter(store?.storeName)}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                {store?.storeOwnerPhoneNumber}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {CapitalizeFirstLetter(store?.address)}
                          </TableCell>

                          <TableCell>
                            <span className="font-semibold text-blue-600 tracking-widest">
                              {store?.otp}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span
                              className={
                                statusInfo?.status === "verified"
                                  ? styles?.amountPaid
                                  : statusInfo?.status === "expired"
                                    ? styles?.expired
                                    : styles?.pending
                              }
                            >
                              {statusInfo?.label}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              {store?.generatedByName ? (
                                <>
                                  <span className="truncate max-w-[150px]">
                                    {CapitalizeFirstLetter(
                                      store?.generatedByName,
                                    )}
                                  </span>
                                  {store?.generatedByPhone && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#666",
                                      }}
                                    >
                                      {Decrypt(store?.generatedByPhone)}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {store?.generatedAt ? (
                              <div className="flex flex-col">
                                <>
                                  {/* Date in DD/MM/YYYY format */}
                                  <span className="">
                                    {new Date(
                                      store?.generatedAt,
                                    ).toLocaleDateString("en-IN")}
                                  </span>

                                  {/* Time in HH:MM AM/PM format */}
                                  <span className="text-gray-600 text-xs mt-0.5">
                                    {new Date(
                                      store?.generatedAt,
                                    ).toLocaleTimeString("en-IN", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })}
                                  </span>
                                </>
                              </div>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </TableCell>

                          <TableCell>
                            {store?.verifiedAt ? (
                              <div className="flex flex-col">
                                <>
                                  <span className="">
                                    {new Date(
                                      store?.verifiedAt,
                                    ).toLocaleDateString("en-IN")}
                                  </span>
                                  <span className="text-gray-600 text-xs mt-0.5">
                                    {new Date(
                                      store?.verifiedAt,
                                    ).toLocaleTimeString("en-IN", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })}
                                  </span>
                                </>
                              </div>
                            ) : (
                              <span className="text-gray-500">
                                Not Verified
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <div className="flex justify-between items-center p-4 border-t bg-white">
              <div className="flex items-center gap-2">
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{ width: 80 }}
                >
                  {[1, 5, 10, 25, 50].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Pagination */}
              <div className="flex items-center gap-4">
                <Pagination
                  count={paginationInfo.totalPages}
                  page={paginationInfo.currentPage}
                  color="primary"
                  onChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default StoreOTP;
